import { supabase } from "@/integrations/supabase/client";

/**
 * Samlar hela projektets data. Används av både projektexporten (spår 3)
 * och tillsynspaketet (spår 1C).
 */

/** Tabeller som hör till ett projekt via kolumnen project_id. */
export const PROJECT_TABLES = [
  "amp_sections",
  "bruksskede_checklists",
  "compliance_tasks",
  "coordination_meetings",
  "document_exports",
  "document_shares",
  "execution_followup_items",
  "external_step_completions",
  "folders",
  "handover_documents",
  "incident_reports",
  "output_documents",
  "parallel_coordination_docs",
  "process_steps",
  "qa_log_entries",
  "residual_use_risks",
  "responsibility_tasks",
  "risk_transfer_items",
  "role_delivery_status",
  "safety_rounds",
  "safety_rule_documents",
  "skr_assessments",
  "stage_plans",
  "use_phase_followups",
  "usage_events",
  "work_area_surveys",
  "work_environment_plans",
  "work_preparations",
] as const;

export const TABLE_LABELS: Record<string, string> = {
  amp_sections: "Arbetsmiljöplan – avsnitt (Dok 12)",
  bruksskede_checklists: "Bruksskede – checklistor",
  compliance_tasks: "Lagkrav och efterlevnad",
  coordination_meetings: "Samordningsmöten (Dok 23)",
  document_exports: "Exporthistorik",
  document_shares: "Externa delningar",
  execution_followup_items: "Uppföljning utförande (Dok 21)",
  external_step_completions: "Externt utförda steg (Dok 04/14/30)",
  folders: "Mappar",
  handover_documents: "Överlämningar (Dok 05/20/22/32)",
  incident_reports: "Tillbud och olyckor (Dok 28)",
  output_documents: "Genererade dokument",
  parallel_coordination_docs: "Parallell samordning",
  process_steps: "Processteg",
  qa_log_entries: "Q&A-logg (Dok 25)",
  residual_use_risks: "Kvarvarande bruksrisker (Dok 31)",
  responsibility_tasks: "Ansvarsuppgifter",
  risk_transfer_items: "Riskövelämning",
  role_delivery_status: "Rollleveranser",
  safety_rounds: "Skyddsronder (Dok 27)",
  safety_rule_documents: "Skydds- och ordningsregler (Dok 15)",
  skr_assessments: "SKR-bedömning (Dok 03)",
  stage_plans: "Skedesplan (Dok 26)",
  use_phase_followups: "Uppföljning bruksskede (Dok 33)",
  usage_events: "Användningshändelser",
  work_area_surveys: "Arbetsområdesutredning (Dok 10)",
  work_environment_plans: "Arbetsmiljöplan",
  work_preparations: "Arbetsberedningar (Dok 24)",
};

export interface ProjectBundle {
  meta: {
    projectId: string;
    projectName: string | null;
    generatedAt: string;
    generatedBy: string | null;
    schemaVersion: number;
  };
  project: Record<string, unknown> | null;
  tables: Record<string, Record<string, unknown>[]>;
  errors: string[];
}

export const collectProjectBundle = async (projectId: string): Promise<ProjectBundle> => {
  const errors: string[] = [];

  const { data: auth } = await supabase.auth.getUser();
  const { data: projectRow } = await supabase
    .from("projects")
    .select("*")
    .eq("id", projectId)
    .maybeSingle();

  const tables: Record<string, Record<string, unknown>[]> = {};

  await Promise.all(
    PROJECT_TABLES.map(async (table) => {
      try {
        const { data, error } = await supabase
          .from(table as never)
          .select("*")
          .eq("project_id", projectId)
          .limit(2000);
        if (error) {
          errors.push(`${table}: ${error.message}`);
          return;
        }
        tables[table] = (data || []) as Record<string, unknown>[];
      } catch (err) {
        errors.push(`${table}: ${(err as Error).message}`);
      }
    })
  );

  return {
    meta: {
      projectId,
      projectName:
        ((projectRow as Record<string, unknown> | null)?.name as string | undefined) ??
        ((projectRow as Record<string, unknown> | null)?.title as string | undefined) ??
        null,
      generatedAt: new Date().toISOString(),
      generatedBy: auth?.user?.email ?? auth?.user?.id ?? null,
      schemaVersion: 1,
    },
    project: (projectRow as Record<string, unknown> | null) ?? null,
    tables,
    errors,
  };
};

/** Enkel CSV-serialisering av en tabell (för tillsynspaketet). */
export const rowsToCsv = (rows: Record<string, unknown>[]): string => {
  if (!rows.length) return "";
  const columns = Array.from(new Set(rows.flatMap((row) => Object.keys(row))));
  const escape = (value: unknown): string => {
    if (value === null || value === undefined) return "";
    const text = typeof value === "object" ? JSON.stringify(value) : String(value);
    return `"${text.replace(/"/g, '""')}"`;
  };
  return [
    columns.join(";"),
    ...rows.map((row) => columns.map((column) => escape(row[column])).join(";")),
  ].join("\n");
};
